import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {MediaHelperService} from '../../core/media-helper.service';
import {SuperObjectModel} from '../../core/definitions/super-object-model';
import {LoggerService} from '../../core/logger.service';
import {RegionParams} from "wavesurfer.js/plugins/regions";
import {AudioWaveformPlayerComponent} from "../../media-players/audio-waveform-player/audio-waveform-player.component";
import {DmsVideoPlayerComponent} from "../dms-video-player/dms-video-player.component";
import {objectTypeToMatSymbol} from "../material-icons";
import {isMediaNotReady, MAX_CONVERSION_POLL_MS, NOT_READY_THUMBNAIL_ICON} from "../thumbnail-fallback-icon";

const PROGRESS_POLL_INTERVAL_MS = 5000;
const STATUS_CHECK_EVERY_NTH_POLL = 6;
const MAX_FAILED_PROGRESS_POLLS = 3;
const CONVERTING_STATUS_KEY = 'TRANS__UPLOAD_STATUS__CONVERTING';
const UPLOAD_STATUS_KEYS: Record<string, string> = {
  init: 'TRANS__UPLOAD_STATUS__INIT',
  convert: CONVERTING_STATUS_KEY,
  converting: CONVERTING_STATUS_KEY
};

@Component({
  selector: 'app-active-media',
  templateUrl: './active-media.component.html',
  styleUrls: ['./active-media.component.scss'],
  standalone: false
})
export class ActiveMediaComponent implements OnChanges, AfterViewInit, OnDestroy {
  // Injections
  private readonly elementRef: ElementRef = inject(ElementRef);
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly mediaHelper: MediaHelperService = inject(MediaHelperService);

  dragItem: any;
  imageSize = {width: 0, height: 0};
  naturalImageSize = {width: 0, height: 0};
  imageLoaded = 0;
  playMedia = false;
  uploadStatus: string;
  validThumbnailSrc = true;
  imageUrl: string;
  segmentRegions: RegionParams[] = [];
  activeRegionId: string | null = null;

  private waitForProgressId: any;
  private killMe = false;
  private pollCount = 0;
  private failedPolls = 0;
  private pollingStartedAt = 0;
  private audioPlayer?: AudioWaveformPlayerComponent;
  private statusLookupMedia?: SuperObjectModel;

  readonly dmsApiUrl = this.mediaHelper.getDmsApiUrl();
  // The same symbol as the attachment's media-type badge in the carousel
  readonly attachmentSymbol = objectTypeToMatSymbol('Attachment');
  // The same hourglass as on the carousel tile of media that is not ready
  readonly notReadyIcon = NOT_READY_THUMBNAIL_ICON;

  @Input() activeMedia: SuperObjectModel;
  @Input() selectedSegmentId: string | null;
  @Input() zoomValue: number;
  @Input() container: HTMLDivElement;
  @Input() fullscreen: boolean;

  @ViewChild(DmsVideoPlayerComponent) private videoPlayer?: DmsVideoPlayerComponent;

  @ViewChild(AudioWaveformPlayerComponent)
  set audioPlayerComponent(player: AudioWaveformPlayerComponent | undefined) {
    this.audioPlayer = player;
    if (player && this.activeRegionId) {
      player.goToRegion(this.activeRegionId);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const segmentChanged = changes.hasOwnProperty('selectedSegmentId');
    const mediaChanged = changes.hasOwnProperty('activeMedia') &&
      changes.activeMedia.previousValue !== changes.activeMedia.currentValue &&
      changes.activeMedia.currentValue !== undefined;

    if (mediaChanged || changes.hasOwnProperty('fullscreen')) {
      this.setMediaInfo().then();
    }

    if (mediaChanged) {
      this.setSegmentRegions();
      this.setActiveImageSize();
      this.resizeActiveImage(this.zoomValue);
    } else if (changes.hasOwnProperty('zoomValue') &&
      changes.zoomValue.previousValue !== changes.zoomValue.currentValue &&
      !!changes.zoomValue.currentValue) {
      this.resizeActiveImage(changes.zoomValue.currentValue);
    }

    if (mediaChanged || segmentChanged) {
      this.setActiveRegion();
    }
  }

  onPlayMedia() {
    this.playMedia = true;
  }

  get objectType() {
    return this.activeMedia?.object_type;
  }

  // Video and audio say "not ready" only once the DMS status says so, not while it is being fetched
  get showNotReady(): boolean {
    if (this.uploadStatus === 'done') {
      return false;
    }
    const waitsForStatus = this.objectType === 'Video' || this.objectType === 'Audio';
    return !waitsForStatus || !!this.uploadStatus;
  }

  // The placeholder image is not shown for media that is not ready, the not-ready block is
  get mediaNotReady(): boolean {
    return isMediaNotReady(this.uploadStatus);
  }

  get conversionProgress(): number | null {
    const progress = this.activeMedia?.$$uploadProgress;
    const finished = this.uploadStatus === 'done' || this.uploadStatus === 'fail';
    return progress > 0 && !finished ? progress : null;
  }

  // Tells what the DMS is doing before it reports a percentage
  get conversionStatusKey(): string | null {
    if (this.conversionProgress !== null) {
      return CONVERTING_STATUS_KEY;
    }
    return UPLOAD_STATUS_KEYS[this.uploadStatus] || null;
  }

  get isPlayable() {
    return !!this.activeMedia?.$$mediaPlaybackUrls;
  }

  // Audio, and video with recording segments, are played in the waveform player so the segments can be shown
  get showWaveformPlayer() {
    return this.objectType === 'Audio'
      || (this.objectType === 'Video' && !!this.activeMedia?.$$mediaSegments?.length);
  }

  get waveformPlaybackUrl() {
    return this.mediaHelper.getWaveformPlaybackUrl(this.activeMedia);
  }

  ngAfterViewInit() {
    this.dragItem = this.getDragItem();
    this.imageLoaded = 1;
  }

  setDefaultImg() {
    this.validThumbnailSrc = false;
    this.lookUpAttachmentStatus().then();
  }

  getDragItem() {
    return this.activeMedia === undefined ? this.elementRef.nativeElement.querySelector('#mediaItem-') :
      this.elementRef.nativeElement.querySelector('#mediaItem-' + this.activeMedia.artifact_id);
  }

  setActiveImageSize() {
    setTimeout(() => {
      this.dragItem = this.getDragItem();
    }, 100);
    setTimeout(() => {
      if (this.dragItem) {
        this.imageSize = this.mediaHelper.setNewImageSize(
          this.container, this.dragItem, this.dragItem.naturalWidth, this.dragItem.naturalHeight);
      } else {
        this.logger.warn('Drag item object not defined');
      }
    }, 200);
  }

  resizeActiveImage(value: number) {
    if (this.imageLoaded === 1 && this.dragItem) {
      this.zoomValue = value;

      if (this.imageSize.width === 0 && this.imageSize.height === 0) {
        this.imageSize.width = this.dragItem.width;
        this.imageSize.height = this.dragItem.height;
      }

      this.naturalImageSize = {
        width: this.dragItem.naturalWidth,
        height: this.dragItem.naturalHeight
      };

      this.mediaHelper.resizeImage(
        this.container,
        this.zoomValue,
        this.dragItem,
        this.naturalImageSize,
        this.imageSize, false);

      if (this.dragItem.width > this.container.offsetWidth ||
        this.dragItem.height > this.container.offsetHeight) {
        this.mediaHelper.imgDrag(
          this.container,
          this.imageLoaded,
          this.dragItem,
          this.imageSize,
          this.zoomValue);
      } else {
        this.stopCallBack();
      }
    }

  }

  refitActiveMedia(): void {
    this.dragItem = this.getDragItem();
    if (!this.dragItem) {
      return;
    }
    this.imageSize = this.mediaHelper.setNewImageSize(
      this.container, this.dragItem, this.dragItem.naturalWidth, this.dragItem.naturalHeight);
    this.resizeActiveImage(this.zoomValue);
  }

  goToStart(): void {
    this.audioPlayer?.goToStart();
  }

  togglePlayback(): void {
    if (this.audioPlayer) {
      this.audioPlayer.togglePlay();
    } else if (this.videoPlayer) {
      this.videoPlayer.togglePlay();
    } else if (this.isPlayable && this.objectType === 'Video') {
      // The video player is not opened yet; it autoplays once shown
      this.onPlayMedia();
    }
  }

  toggleSegmentPlayback(segmentId: string): void {
    if (segmentId) {
      this.audioPlayer?.toggleRegionPlayback(segmentId);
    }
  }

  stopCallBack(): void {
    this.mediaHelper.stopCallBack();
  }

  ngOnDestroy() {
    this.stopCallBack();
    this.imageLoaded = 0;
    this.killMe = true;
    this.stopWaitingForProgress();
  }

  private async setMediaInfo() {
    this.stopWaitingForProgress();
    this.playMedia = false;
    this.uploadStatus = '';
    this.statusLookupMedia = undefined;
    if (!this.activeMedia) {
      this.imageUrl = '';
      this.validThumbnailSrc = false;
      return;
    }
    this.imageUrl = this.sourceImageUrl(this.activeMedia);
    this.validThumbnailSrc = !!this.imageUrl;

    const media = this.activeMedia;
    const mediaObjType = media.object_type;
    if (mediaObjType === 'Video' || mediaObjType === 'Audio') {
      if (!media.$$mediaPlaybackUrls) {
        // The DMS image may not exist until the media is converted, so wait for the status
        this.validThumbnailSrc = false;
        const status = await this.mediaHelper.getMediaUploadStatus(media);
        if (this.isStale(media)) {
          return;
        }
        this.setUploadStatus(media, status);
        this.validThumbnailSrc = !!this.imageUrl && !isMediaNotReady(status);
        if (status === 'done') {
          await this.loadPlaybackUrls(media);
        } else if (isMediaNotReady(status)) {
          this.startProgressPolling(media);
        }
      } else {
        this.setUploadStatus(media, 'done');
      }
    } else if (mediaObjType === 'Attachment' && !this.imageUrl) {
      await this.lookUpAttachmentStatus();
    }

    this.updatePlayMedia();
  }

  private setSegmentRegions() {
    const segments = this.activeMedia?.$$mediaSegments || [];
    this.segmentRegions = segments.map(segment => ({
      id: segment.segmentId,
      content: segment.remarks || segment.name,
      start: segment.start / 1000,
      end: segment.end / 1000
    }));
  }

  private setActiveRegion(): void {
    const segmentId = this.selectedSegmentId || null;
    const known = !!segmentId && this.segmentRegions.some(region => region.id === segmentId);
    this.activeRegionId = known ? segmentId : null;
    if (this.activeRegionId) {
      this.audioPlayer?.goToRegion(this.activeRegionId);
    }
  }

  // An attachment has no thumbnail until the DMS has converted it; looked up once per media
  private async lookUpAttachmentStatus() {
    const media = this.activeMedia;
    if (media?.object_type !== 'Attachment' || this.statusLookupMedia === media) {
      return;
    }
    this.statusLookupMedia = media;
    const status = await this.mediaHelper.getMediaUploadStatus(media);
    if (!this.isStale(media) && isMediaNotReady(status)) {
      this.setUploadStatus(media, status);
      this.startProgressPolling(media);
    }
  }

  private startProgressPolling(media: SuperObjectModel) {
    this.pollCount = 0;
    this.failedPolls = 0;
    this.pollingStartedAt = Date.now();
    this.waitForProgress(media);
  }

  private waitForProgress(media: SuperObjectModel) {
    this.stopWaitingForProgress();
    this.waitForProgressId = setTimeout(() => this.pollProgress(media), PROGRESS_POLL_INTERVAL_MS);
  }

  private stopWaitingForProgress() {
    if (this.waitForProgressId) {
      clearTimeout(this.waitForProgressId);
      this.waitForProgressId = null;
    }
  }

  private async pollProgress(media: SuperObjectModel) {
    if (this.isStale(media) || Date.now() - this.pollingStartedAt >= MAX_CONVERSION_POLL_MS) {
      return;
    }
    const progress = await this.mediaHelper.getMediaUploadProgress(media);
    if (this.isStale(media)) {
      return;
    }
    this.pollCount++;
    this.registerProgress(media, progress);
    if (this.failedPolls >= MAX_FAILED_PROGRESS_POLLS) {
      return;
    }
    if (await this.isConversionFinished(media, progress)) {
      return;
    }
    this.waitForProgress(media);
  }

  private registerProgress(media: SuperObjectModel, progress: number | null) {
    if (progress === null) {
      this.failedPolls++;
      return;
    }
    this.failedPolls = 0;
    media.$$uploadProgress = Math.max(media.$$uploadProgress || 0, progress);
  }

  private async isConversionFinished(media: SuperObjectModel, progress: number | null): Promise<boolean> {
    if (progress !== 100 && this.pollCount % STATUS_CHECK_EVERY_NTH_POLL !== 0) {
      return false;
    }
    const status = await this.mediaHelper.getMediaUploadStatus(media);
    if (this.isStale(media)) {
      return true;
    }
    if (status === 'done') {
      this.refreshThumbnailUrl(media);
      await this.showConvertedMedia(media);
      return true;
    }
    if (status === 'fail') {
      this.setUploadStatus(media, 'fail');
      return true;
    }
    if (isMediaNotReady(status)) {
      // Keeps the shown status from going stale, e.g. init -> converting
      this.setUploadStatus(media, status);
    }
    // The DMS can report 100 % before its status turns done, so keep polling until it does
    return false;
  }

  // An attachment has nothing to play, so it only gets a fresh thumbnail url
  private async showConvertedMedia(media: SuperObjectModel) {
    if (media.object_type !== 'Attachment') {
      await this.loadPlaybackUrls(media);
      return;
    }
    this.setUploadStatus(media, 'done');
    const url = this.sourceImageUrl(media);
    if (url) {
      this.imageUrl = this.mediaHelper.getImageUrlWithRandom(url);
      this.validThumbnailSrc = true;
    }
  }

  private sourceImageUrl(media: SuperObjectModel): string {
    return this.fullscreen ? media.$$maxImageUrl : media.$$largeImageUrl;
  }

  private async loadPlaybackUrls(media: SuperObjectModel) {
    const urls = await this.mediaHelper.getMediaPlaybackUrls(media);
    if (this.isStale(media)) {
      return;
    }
    media.$$mediaPlaybackUrls = urls;
    this.setUploadStatus(media, 'done');
    this.showThumbnailIfAvailable();
    this.updatePlayMedia();
  }

  // The DMS thumbnail can be missing while converting; a fresh url makes the browser fetch it again
  private refreshThumbnailUrl(media: SuperObjectModel) {
    if (media.$$thumbUrl) {
      media.$$thumbUrl = this.mediaHelper.getImageUrlWithRandom(media.$$thumbUrl);
    }
  }

  // Also kept on the media item, so its carousel tile can show that it is not ready yet
  private setUploadStatus(media: SuperObjectModel, status: string) {
    this.uploadStatus = status;
    media.$$dmsStatus = status;
  }

  private showThumbnailIfAvailable() {
    this.validThumbnailSrc = !!this.imageUrl;
  }

  private isStale(media: SuperObjectModel): boolean {
    return this.killMe || this.activeMedia !== media;
  }

  private updatePlayMedia() {
    // Load the waveform player automatically, so segments can be selected right away
    this.playMedia = this.showWaveformPlayer && !!this.activeMedia.$$mediaPlaybackUrls;
  }

}
